import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthLite } from "@/context/AuthLite";
import { useIsMobile } from "@/hooks/use-mobile";
import Loading from "@/components/Loading";
import { FullPageMessage } from "@/components/FullPageMessage";
import { PageTitleBar } from "@/components/ui/PageTitleBar";
import { SelectionLiveRegion } from "@/components/ui/selection-live-region";
import { useSelectionKeyboardShortcuts } from "@/hooks/use-selection-keyboard-shortcuts";
import { apiUrl } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerPopover } from "@/components/ui/date-picker-popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  Trash2,
  Download,
  MoveRight,
  Upload,
  X,
  Plus,
  ChevronDown,
  Filter,
  MoreHorizontal,
  Pencil,
  AlertCircle,
} from "lucide-react";
import { CheckSquare } from "lucide-react";
import type { ImageFolderPhoto, SubfolderType, PrelisType, ImageFolderCategory } from "@shared/api";
import PhotoGrid from "@/components/images/PhotoGrid";
import PhotoViewer from "@/components/images/PhotoViewer";
import LookupSelect from "@/components/images/LookupSelect";
import { generateAndUploadThumbnail } from "@/lib/image-thumbnail";
import { FolderPhotoUploadQueuedLocallyError } from "@/lib/image-upload-outbox";
import { uploadImages } from "@/lib/image-upload";
import { notify } from "@/lib/notify";
import { runFolderPhotoUploadFollowup } from "@/lib/folder-photo-upload-followup";
import { toast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Loader2, Check, ChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { FabMenu } from "@/components/ui/fab-menu";
import { ImagesSectionTabs } from "@/components/images/ImagesSectionTabs";
import { AppSheet } from "@/components/ui/app-sheet";
import ImageFilters from "@/components/images/ImageFilters";
import { readImageFiltersFromQuery, writeImageFiltersToQuery } from "@/lib/image-filter-query";
import { useGalleryPhotoFacets } from "@/hooks/use-gallery-photo-facets";
import { filterImageFiles, dataTransferHasFilePayload } from "@/lib/filter-image-drop-files";
import { uploadImageFilesToCentralGallery } from "@/lib/upload-image-files-to-central-gallery";
import { hapticTap } from "@/lib/haptics";

export default function ImagesFolderPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { folderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  const galleryFacetsQuery = useGalleryPhotoFacets();
  const { user } = useAuthLite();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initialFilterState = readImageFiltersFromQuery(searchParams);
  const [filters, setFilters] = useState(initialFilterState.filters);
  const [searchName, setSearchName] = useState(initialFilterState.searchName);
  const [dateFilterEnabled, setDateFilterEnabled] = useState(initialFilterState.dateFilterEnabled);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Selection state
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [selectionModeEnabled, setSelectionModeEnabled] = useState(false);
  const selectionMode = selectionModeEnabled;
  const desktopSelectionMode = !isMobile && selectionModeEnabled;
  const bulkHeaderDismissRef = useRef<HTMLButtonElement>(null);

  const exitPhotoSelectionMode = useCallback(() => {
    setSelectionModeEnabled(false);
    setSelectedPhotos(new Set());
  }, []);
  
  // Drag selection state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragEnd, setDragEnd] = useState<{ x: number; y: number } | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  // Photo viewer state
  const [viewerPhoto, setViewerPhoto] = useState<{ photo: ImageFolderPhoto; index: number; allPhotos: ImageFolderPhoto[] } | null>(null);
  const [uploadSubfolderType, setUploadSubfolderType] = useState<SubfolderType>("bemeres");
  const [uploadTargetDialogOpen, setUploadTargetDialogOpen] = useState(false);
  const [pendingDialogUploadFiles, setPendingDialogUploadFiles] = useState<File[]>([]);
  const [dialogTargetSubfolder, setDialogTargetSubfolder] = useState<SubfolderType>("bemeres");
  const [galleryOnlyUploadBusy, setGalleryOnlyUploadBusy] = useState(false);
  const [fileDragHighlight, setFileDragHighlight] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const autoThumbnailGenStartedRef = useRef(false);

  // Edit folder dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showMarketingWebhookDialog, setShowMarketingWebhookDialog] = useState(false);
  const [marketingWebhookSending, setMarketingWebhookSending] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    panelStructure: "",
    color: "",
    prelisType: "",
    brand: "",
    installDate: "",
    dealId: "",
    category: "bemeres" as ImageFolderCategory,
  });
  const isAdmin = user?.role === "Admin";

  // Deal search state for edit dialog
  const [editDealSearchOpen, setEditDealSearchOpen] = useState(false);
  const [editDealSearchValue, setEditDealSearchValue] = useState("");

  // Fetch deals for search - use the same query key as useGlobalSearch to share cache
  const boardQuery = useQuery({
    queryKey: queryKeys.crmBoard,
    queryFn: async () => {
      const r = await fetch(apiUrl("/api/crm/board"), {
        credentials: "include",
      });
      if (!r.ok) {
        console.error("[Deals Query] Failed to fetch board:", r.status);
        throw new Error(`Board fetch failed with status ${r.status}`);
      }
      const data = await r.json();
      // Ensure deals is always an array
      const deals = Array.isArray(data.deals) ? data.deals : [];
      console.log("[Deals Query] Board response:", { 
        hasDeals: deals.length > 0, 
        dealsIsArray: Array.isArray(deals),
        dealsLength: deals.length,
        firstDeal: deals.length > 0 ? deals[0] : null
      });
      return {
        ...data,
        deals: deals, // Ensure deals is always an array
      };
    },
    retry: (failureCount, error) => {
      // Retry up to 3 times with exponential backoff
      if (failureCount < 3) {
        return true;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s, max 30s
    staleTime: 5 * 60 * 1000, // 5 minutes - same as useGlobalSearch
    // Don't cache errors - let React Query handle retries
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  // Extract deals from board data
  const dealsQuery = {
    data: Array.isArray(boardQuery.data?.deals) ? boardQuery.data.deals : [],
    isLoading: boardQuery.isLoading,
    isError: boardQuery.isError,
    error: boardQuery.error,
  } as { data: Array<{ id: string; title: string; width?: string; height?: string; color?: string; surface?: string; emboss?: string; closingDate?: string }>; isLoading: boolean; isError: boolean; error: any };

  // Fetch folder with photos
  const folderQuery = useQuery({
    queryKey: ["image-folder", folderId],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/images/folders/${folderId}`), {
        credentials: "include",
      });
      if (!r.ok) throw new Error(t("imagesFolder.errorLoadFolder"));
      const data = await r.json();
      return data.folder;
    },
    enabled: !!folderId,
  });

  const dateRangeQuery = useQuery({
    queryKey: ["image-folders-date-range"],
    queryFn: async () => {
      const r = await fetch(apiUrl("/api/images/folders/date-range"), { credentials: "include" });
      if (!r.ok) return { minDate: undefined, maxDate: undefined };
      const data = await r.json();
      return { minDate: data.minDate as string | undefined, maxDate: data.maxDate as string | undefined };
    },
  });

  useEffect(() => {
    const next = writeImageFiltersToQuery(searchParams, { filters, searchName, dateFilterEnabled });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [filters, searchName, dateFilterEnabled, searchParams, setSearchParams]);

  // Update folder mutation
  const updateFolder = useMutation({
    mutationFn: async (data: any) => {
      const r = await fetch(apiUrl(`/api/images/folders/${folderId}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error(t("imagesFolderExtra2.updateFolderFailed"));
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["image-folder", folderId] });
      qc.invalidateQueries({ queryKey: ["image-folders"] });
      qc.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
      setShowEditDialog(false);
    },
  });

  // Delete photo mutation
  const deletePhoto = useMutation({
    mutationFn: async (photoId: string) => {
      const r = await fetch(apiUrl(`/api/images/folders/${folderId}/photos/${photoId}`), {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error(t("imagesFolder.errorDeletePhoto"));
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["image-folder", folderId] });
      qc.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
      setSelectedPhotos(new Set());
    },
  });

  // Bulk delete
  const bulkDelete = useMutation({
    mutationFn: async (photoIds: string[]) => {
      const r = await fetch(apiUrl(`/api/images/folders/${folderId}/photos/batch`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ photoIds }),
      });
      if (!r.ok) throw new Error(t("imagesFolder.errorDeletePhotos"));
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["image-folder", folderId] });
      qc.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
      setSelectedPhotos(new Set());
    },
  });

  // Move photos
  const movePhotos = useMutation({
    mutationFn: async ({ photoIds, targetSubfolderType }: { photoIds: string[]; targetSubfolderType: SubfolderType }) => {
      const r = await fetch(apiUrl(`/api/images/folders/${folderId}/photos/move`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ photoIds, targetSubfolderType }),
      });
      if (!r.ok) throw new Error(t("imagesFolder.errorMovePhotos"));
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["image-folder", folderId] });
      setSelectedPhotos(new Set());
    },
  });

  // Upload queue state
  const [uploadQueue, setUploadQueue] = useState<
    {
      id: string;
      file: File;
      status: "queued" | "uploading" | "done" | "error";
      progress: number;
    }[]
  >([]);

  // Upload photos
  const uploadPhotos = useMutation({
    mutationFn: async ({ files, subfolderType, fileIds }: { files: File[]; subfolderType: SubfolderType; fileIds: string[] }) => {
      if (!folderId) {
        throw new Error(t("imagesFolder.errorUploadPhotos"));
      }
      const url = apiUrl(`/api/images/folders/${folderId}/photos`);
      const res = await uploadImages({
        uploadUrl: url,
        files,
        extraFields: { subfolderType },
        context: { folderId },
      });

      // Nothing reached the server and everything was queued -> signal the queued path.
      if (res.uploaded === 0 && res.queued > 0) {
        throw new FolderPhotoUploadQueuedLocallyError();
      }
      if (res.queued > 0) {
        notify.info(
          t("uploadOutbox.partialSuccess", { queued: res.queued, ok: res.uploaded }),
          t("uploadOutbox.queuedBatchDesc"),
        );
      }
      return { data: { photos: res.photos }, fileIds };
    },
    onSuccess: async (result, variables) => {
      console.log(`[UPLOAD] onSuccess called - result:`, result, `variables:`, variables);
      qc.invalidateQueries({ queryKey: ["image-folder", folderId] });
      qc.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });

      // Mark only the uploaded files as done
      setUploadQueue((q) => q.map((item) => 
        result.fileIds.includes(item.id)
          ? { ...item, status: "done" as const, progress: 100 }
          : item
      ));
      
      // Get the uploaded photos to check for missing thumbnails
      const uploadedPhotos = result.data?.photos || [];
      console.log(`[Thumbnail] Upload success - result.data:`, result.data);
      console.log(`[Thumbnail] Upload success - photos: ${uploadedPhotos.length}, files: ${variables.files?.length || 0}`);
      console.log(`[Thumbnail] Photos without thumbnail:`, uploadedPhotos.filter((p: ImageFolderPhoto) => !p.thumbnail_url).length);
      console.log(`[Thumbnail] Photo filenames:`, uploadedPhotos.map((p: ImageFolderPhoto) => p.file_name));
      console.log(`[Thumbnail] File names:`, variables.files?.map(f => f.name));
      
      if (uploadedPhotos.length > 0 && variables.files && variables.files.length > 0 && folderId) {
        void runFolderPhotoUploadFollowup(qc, folderId, result.data, variables.files).catch((err) => {
          console.error("[Upload] Follow-up thumbnails / refresh:", err);
        });
      }
      
      // Check if all files are done, then clear queue after 2 seconds
      setTimeout(() => {
        setUploadQueue((q) => {
          const allDone = q.every((item) => item.status === "done" || item.status === "error");
          return allDone ? [] : q;
        });
      }, 2000);
    },
    onError: (error, variables) => {
      if (error instanceof FolderPhotoUploadQueuedLocallyError) {
        notify.info(
          t("uploadOutbox.queuedBatch", { count: variables.files.length }),
          t("uploadOutbox.queuedBatchDesc"),
        );
        setUploadQueue((q) => q.filter((item) => !variables.fileIds.includes(item.id)));
        return;
      }
      setUploadQueue((q) =>
        q.map((item) =>
          variables.fileIds.includes(item.id) ? { ...item, status: "error" as const } : item,
        ),
      );
      console.error("[Upload] Error:", error);
    },
  });

  const startFolderPhotoUploadQueue = (fileArray: File[], subfolderType: SubfolderType) => {
    if (fileArray.length === 0) return;
    const items = fileArray.map((f) => ({
      id: `${f.name}_${f.size}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      file: f,
      status: "queued" as const,
      progress: 0,
    }));
    setUploadQueue((q) => [...q, ...items]);
    const BATCH_SIZE = 3;
    const batches: { files: File[]; ids: string[] }[] = [];
    for (let i = 0; i < fileArray.length; i += BATCH_SIZE) {
      const batchFiles = fileArray.slice(i, i + BATCH_SIZE);
      const batchIds = items.slice(i, i + BATCH_SIZE).map((item) => item.id);
      batches.push({ files: batchFiles, ids: batchIds });
    }
    batches.forEach((batch, batchIndex) => {
      setTimeout(() => {
        setUploadQueue((q) =>
          q.map((item) =>
            batch.ids.includes(item.id) ? { ...item, status: "uploading" as const } : item
          )
        );
        uploadPhotos.mutate({
          files: batch.files,
          subfolderType,
          fileIds: batch.ids,
        });
      }, batchIndex * 1000);
    });
  };

  const addToUploadQueue = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const fileArray = filterImageFiles(Array.from(files));
    if (fileArray.length === 0) return;
    startFolderPhotoUploadQueue(fileArray, uploadSubfolderType);
  };

  const openUploadTargetDialog = (rawFiles: File[], suggestedSubfolder: SubfolderType) => {
    const fileArray = filterImageFiles(rawFiles);
    if (fileArray.length === 0) {
      toast({
        title: t("imagesFolder.dropNoImages"),
        variant: "destructive",
      });
      return;
    }
    setDialogTargetSubfolder(suggestedSubfolder);
    setPendingDialogUploadFiles(fileArray);
    setUploadTargetDialogOpen(true);
  };

  const closeUploadTargetDialog = () => {
    setUploadTargetDialogOpen(false);
    setPendingDialogUploadFiles([]);
  };

  const confirmUploadToCurrentFolder = () => {
    const files = pendingDialogUploadFiles;
    if (files.length === 0) return;
    setUploadSubfolderType(dialogTargetSubfolder);
    closeUploadTargetDialog();
    startFolderPhotoUploadQueue(files, dialogTargetSubfolder);
  };

  const confirmUploadToGalleryOnly = async () => {
    const files = pendingDialogUploadFiles;
    if (files.length === 0) return;
    setGalleryOnlyUploadBusy(true);
    try {
      const res = await uploadImageFilesToCentralGallery(files, qc);
      closeUploadTargetDialog();
      if (res.queued > 0 && res.uploaded === 0) {
        notify.info(
          t("uploadOutbox.queuedBatch", { count: res.queued }),
          t("uploadOutbox.queuedBatchDesc"),
        );
      } else {
        toast({
          title: t("imagesFolder.dropGallerySuccess"),
          description: t("imagesFolder.dropGallerySuccessDesc", { count: files.length }),
        });
      }
    } catch (e) {
      toast({
        title: t("imagesFolder.dropGalleryError"),
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setGalleryOnlyUploadBusy(false);
    }
  };

  // Generate thumbnails for existing photos that don't have one (used automatically on folder load)
  const generateThumbnailsForPhotosWithoutThumbnails = async (photos: ImageFolderPhoto[]) => {
    if (!folderId || photos.length === 0) return;
    
    const BATCH_SIZE = 5; // Limit concurrent work to avoid overwhelming network
    for (let i = 0; i < photos.length; i += BATCH_SIZE) {
      const batch = photos.slice(i, i + BATCH_SIZE);
      for (const photo of batch) {
        try {
          const imageResponse = await fetch(photo.file_url);
          if (!imageResponse.ok) continue;
          
          const imageBlob = await imageResponse.blob();
          const imageFile = new File([imageBlob], photo.file_name, { type: imageBlob.type });
          
          await generateAndUploadThumbnail(
            imageFile,
            folderId,
            photo.subfolder_type,
            photo.id
          );
        } catch (error) {
          console.error(`[Thumbnail] Error generating thumbnail for ${photo.file_name}:`, error);
        }
      }
      qc.invalidateQueries({ queryKey: ["image-folder", folderId] });
    }
  };

  const handleSelectPhoto = (photoId: string, checked: boolean) => {
    setSelectedPhotos((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(photoId);
      } else {
        next.delete(photoId);
      }
      return next;
    });
  };

  const handleBulkDelete = () => {
    if (selectedPhotos.size === 0) return;
    if (confirm(`Biztosan törölni szeretnéd a kiválasztott ${selectedPhotos.size} fotót?`)) {
      bulkDelete.mutate(Array.from(selectedPhotos));
    }
  };

  const handleBulkMove = (targetSubfolderType: SubfolderType) => {
    if (selectedPhotos.size === 0) return;
    movePhotos.mutate({
      photoIds: Array.from(selectedPhotos),
      targetSubfolderType,
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    addToUploadQueue(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePhotoClick = (photo: ImageFolderPhoto, index: number, allPhotos: ImageFolderPhoto[]) => {
    setViewerPhoto({ photo, index, allPhotos });
  };

  const handleViewerNavigate = (newIndex: number) => {
    if (viewerPhoto) {
      setViewerPhoto({
        ...viewerPhoto,
        photo: viewerPhoto.allPhotos[newIndex],
        index: newIndex,
      });
    }
  };

  // Drag selection handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only work in selection mode
    if (!selectionModeEnabled) return;
    
    // Only start drag selection if clicking on empty space (not on photos, buttons, checkboxes, etc.)
    const target = e.target as HTMLElement;
    // Don't start drag if clicking on a photo or its children
    if (target.closest('[data-photo-id]') || target.closest('button') || target.closest('[role="checkbox"]') || target.closest('input')) {
      return;
    }
    
    if (e.button === 0) { // Left mouse button
      setIsDragging(true);
      const rect = gridContainerRef.current?.getBoundingClientRect();
      if (rect) {
        setDragStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        setDragEnd({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !dragStart) return;
    
    const rect = gridContainerRef.current?.getBoundingClientRect();
    if (rect) {
      setDragEnd({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  };

  const handleMouseUp = () => {
    if (!isDragging || !dragStart || !dragEnd) {
      setIsDragging(false);
      setDragStart(null);
      setDragEnd(null);
      return;
    }

    // Calculate selection box
    const minX = Math.min(dragStart.x, dragEnd.x);
    const maxX = Math.max(dragStart.x, dragEnd.x);
    const minY = Math.min(dragStart.y, dragEnd.y);
    const maxY = Math.max(dragStart.y, dragEnd.y);

    // Find all photos that intersect with the selection box
    const selectedIds = new Set<string>();
    const gridElements = gridContainerRef.current?.querySelectorAll('[data-photo-id]');
    
    gridElements?.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const containerRect = gridContainerRef.current?.getBoundingClientRect();
      if (containerRect) {
        const elX = rect.left - containerRect.left;
        const elY = rect.top - containerRect.top;
        const elWidth = rect.width;
        const elHeight = rect.height;

        // Check if element intersects with selection box
        if (
          elX < maxX &&
          elX + elWidth > minX &&
          elY < maxY &&
          elY + elHeight > minY
        ) {
          const photoId = el.getAttribute('data-photo-id');
          if (photoId) {
            selectedIds.add(photoId);
          }
        }
      }
    });

    // Add selected photos to selection
    if (selectedIds.size > 0) {
      setSelectedPhotos((prev) => {
        const next = new Set(prev);
        selectedIds.forEach((id) => next.add(id));
        return next;
      });
    }

    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
  };

  // Global mouse up handler for drag selection (in case mouse leaves the container)
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseUp = () => {
        handleMouseUp();
      };
      window.addEventListener('mouseup', handleGlobalMouseUp);
      return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }
  }, [isDragging, dragStart, dragEnd]);

  // Load folder data into edit form when folder is loaded
  useEffect(() => {
    if (folderQuery.data) {
      const cat = folderQuery.data.category;
      setEditForm({
        name: folderQuery.data.name || "",
        panelStructure: folderQuery.data.panel_structure || "",
        color: folderQuery.data.color || "",
        prelisType: folderQuery.data.prelis_type || "",
        brand: folderQuery.data.brand || "",
        installDate: folderQuery.data.install_date || "",
        dealId: folderQuery.data.deal_id || "",
        category: (cat && ["bemeres", "realizacio", "egyeb"].includes(cat) ? cat : "bemeres") as ImageFolderCategory,
      });
    }
  }, [folderQuery.data]);

  // Load deal data and populate form fields
  const loadDealData = async (dealId: string) => {
    try {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}`), {
        credentials: "include",
      });
      if (!r.ok) {
        console.error("Failed to load deal:", r.status);
        return;
      }
      const data = await r.json();
      const deal = data.deal || data;

      // Map deal fields to form fields (same variables as in deals)
      const panelStructure = deal.surface || null; // Use surface directly, not width x height
      const color = deal.color || null;
      // Handle emboss field - extract prelis type from deal.emboss
      // Supports patterns like "M", "L", "W", "G", "Prelis M", "Prelis L", etc.
      let prelisType: string | undefined = undefined;
      if (deal.emboss) {
        const embossUpper = String(deal.emboss).toUpperCase().trim();
        // Match "PRELIS X" pattern first
        const prelisMatch = embossUpper.match(/PRELIS\s+([A-Z0-9]+)/);
        if (prelisMatch && prelisMatch[1]) {
          prelisType = prelisMatch[1];
        } else if (embossUpper.length <= 5 && /^[A-Z0-9]+$/.test(embossUpper)) {
          // If it's a short alphanumeric value (likely a prelis type), use it
          prelisType = embossUpper;
        }
      }
      const closingDate = deal.closingDate ? deal.closingDate.split("T")[0] : null; // Extract date part from ISO string

      setEditForm((prev) => ({
        ...prev,
        dealId: dealId,
        panelStructure: panelStructure || prev.panelStructure,
        color: color || prev.color,
        prelisType: prelisType || prev.prelisType,
        installDate: closingDate || prev.installDate,
      }));
    } catch (error) {
      console.error("Error loading deal data:", error);
    }
  };

  const folder = folderQuery.data;
  const folderMatchesFilters = useMemo(() => {
    if (!folder) return true;
    const normalizedSearch = searchName.trim().toLowerCase();
    if (normalizedSearch && !String(folder.name || "").toLowerCase().includes(normalizedSearch)) {
      return false;
    }
    const valueMatches = (value: string | null | undefined, selected?: string | string[]) => {
      if (!selected) return true;
      const selectedValues = (Array.isArray(selected) ? selected : [selected]).map((v) => String(v).trim());
      const normalizedValue = (value || "Ismeretlen").trim();
      return selectedValues.includes(normalizedValue);
    };
    if (!valueMatches(folder.panel_structure, filters.panelStructure)) return false;
    if (!valueMatches(folder.brand, filters.brand)) return false;
    if (!valueMatches(folder.folder_type, filters.folderType)) return false;
    if (!valueMatches(folder.model, filters.model)) return false;
    if (!valueMatches(folder.color, filters.color)) return false;
    if (!valueMatches(folder.prelis_type, filters.prelisType as string | string[] | undefined)) return false;
    if (!valueMatches(folder.category, filters.category as string | string[] | undefined)) return false;
    if (dateFilterEnabled && filters.dateFrom && folder.install_date && folder.install_date < filters.dateFrom) return false;
    if (dateFilterEnabled && filters.dateTo && folder.install_date && folder.install_date > filters.dateTo) return false;
    return true;
  }, [folder, filters, searchName, dateFilterEnabled]);
  const allPhotos: ImageFolderPhoto[] = folder
    ? [
        ...(folder.photos.bemeres || []),
        ...(folder.photos.munkaink || []),
        ...(folder.photos.marketing || []),
      ]
    : [];

  useSelectionKeyboardShortcuts({
    enabled: selectionModeEnabled,
    onExit: exitPhotoSelectionMode,
    onSelectAllInView: () => {
      if (allPhotos.length === 0) return;
      setSelectedPhotos(new Set(allPhotos.map((p) => p.id)));
    },
  });

  useEffect(() => {
    if (!selectionModeEnabled) return;
    const id = requestAnimationFrame(() => bulkHeaderDismissRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [selectionModeEnabled]);

  // Bemérésre: flat list with auto labels – MUST be before early returns (hooks rule)
  const bemeresPhotosWithLabels = useMemo(() => {
    const bemeres = folder?.photos?.bemeres || [];
    type PhotoWithSlug = ImageFolderPhoto & { product_slug?: string | null };
    const sorted = [...bemeres].sort((a, b) => {
      const sa = ((a as PhotoWithSlug).product_slug ?? "").toString();
      const sb = ((b as PhotoWithSlug).product_slug ?? "").toString();
      if (sa !== sb) return sa === "" ? -1 : sb === "" ? 1 : sa.localeCompare(sb);
      return (a.order_index ?? 0) - (b.order_index ?? 0);
    });
    let lastSlug: string | undefined;
    let indexInProduct = 0;
    return sorted.map((p) => {
      const slug = ((p as PhotoWithSlug).product_slug ?? "").toString();
      if (slug !== lastSlug) {
        lastSlug = slug;
        indexInProduct = 1;
      } else {
        indexInProduct++;
      }
      const prefix = slug || t("imagesFolderExtra.bemeresUnspecifiedLabel");
      const _displayLabel = `${prefix}-${indexInProduct}`;
      return { ...p, _displayLabel } as ImageFolderPhoto & { _displayLabel: string };
    });
  }, [folder?.photos?.bemeres, t]);

  const photoLabelById = useMemo(() => {
    const m = new Map<string, string>();
    bemeresPhotosWithLabels.forEach((p) => m.set(p.id, p._displayLabel));
    return m;
  }, [bemeresPhotosWithLabels]);

  // Auto-generate thumbnails for photos that don't have one (runs once when folder loads)
  const prevFolderIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!folderId) return;
    if (prevFolderIdRef.current !== folderId) {
      prevFolderIdRef.current = folderId;
      autoThumbnailGenStartedRef.current = false;
    }
    const folder = folderQuery.data;
    if (!folder || autoThumbnailGenStartedRef.current) return;
    
    const allPhotos: ImageFolderPhoto[] = [
      ...(folder.photos?.bemeres || []),
      ...(folder.photos?.munkaink || []),
      ...(folder.photos?.marketing || []),
    ];
    const photosWithoutThumbnails = allPhotos.filter((p) => !p.thumbnail_url);
    
    if (photosWithoutThumbnails.length === 0) return;
    
    autoThumbnailGenStartedRef.current = true;
    toast({
      title: t("imagesFolderExtra.generatingThumbnails"),
    });
    
    generateThumbnailsForPhotosWithoutThumbnails(photosWithoutThumbnails);
  }, [folderQuery.data, folderId, t, qc]);

  if (folderQuery.isLoading) {
    return (
      <>
        {/* ux-consistency: loading/empty C */}
        <Loading label={t("imagesFolder.loading")} className="min-h-[50vh]" />
      </>
    );
  }

  if (folderQuery.isError || !folder) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
        <div className="relative z-20 flex h-[60px] flex-shrink-0 overflow-hidden border-b bg-background">
          <PageTitleBar
            className="h-full border-0"
            rowClassName="h-full min-h-0 items-center"
            title={t("images.title")}
          />
        </div>
        <div className="flex min-h-0 flex-1 flex-col">
          <FullPageMessage
            variant="embedded"
            icon={AlertCircle}
            title={t("imagesFolder.errorLoadFolder")}
            subtitle={t("common.tryAgainLater")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className={cn("flex-shrink-0 px-3 py-3 sm:px-4 sm:py-4 relative overflow-hidden bg-background border-b z-20", isMobile ? "min-h-[60px]" : "h-[60px]")}>
        {/* Normal Header */}
        <div
          className={cn(
            "absolute inset-0 flex items-center transition-all duration-300 ease-in-out",
            desktopSelectionMode
              ? "opacity-0 translate-y-[-100%] pointer-events-none"
              : "opacity-100 translate-y-0",
          )}
        >
          <PageTitleBar
            className="w-full min-w-0 border-0"
            rowClassName={cn("min-h-0 flex-1 items-center gap-2", isMobile && "flex-wrap")}
            left={
              <Button variant="ghost" size="sm" onClick={() => navigate("/images/projects")} className="shrink-0">
                <ArrowLeft className="h-4 w-4 md:mr-2" />
                {!isMobile && t("imagesFolder.back")}
              </Button>
            }
            title={folder.name}
            titleClassName={cn(
              "!pl-0 !font-semibold text-base md:text-xl",
              isMobile && "max-w-[140px]",
            )}
            right={
              <div className={cn("flex shrink-0 items-center gap-2", isMobile && "flex-wrap justify-end")}>
                {isMobile && allPhotos.length > 0 ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 shrink-0 p-0"
                    type="button"
                    aria-label={selectionModeEnabled ? t("images.cancelSelection") : t("images.selectPhotos")}
                    onClick={() => {
                      hapticTap(12);
                      if (selectionModeEnabled) exitPhotoSelectionMode();
                      else setSelectionModeEnabled(true);
                    }}
                  >
                    {selectionModeEnabled ? <X className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}
                  </Button>
                ) : !isMobile && !selectionModeEnabled ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 text-xs md:px-3 md:text-sm"
                    type="button"
                    onClick={() => setSelectionModeEnabled(true)}
                  >
                    <span className="hidden sm:inline">{t("images.selection")}</span>
                    <span className="sm:hidden">{t("images.selectionShort")}</span>
                  </Button>
                ) : null}
                {isMobile ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 p-0"
                    onClick={() => setFilterSheetOpen(true)}
                    aria-label={t("common.filterLabel")}
                  >
                    <Filter className="h-4 w-4" />
                  </Button>
                ) : null}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                {isAdmin && !isMobile ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 text-xs md:px-3 md:text-sm"
                    onClick={() => setShowMarketingWebhookDialog(true)}
                  >
                    {t("imagesFolder.resendMarketingWebhook")}
                  </Button>
                ) : null}
              </div>
            }
          />
        </div>

        {/* Desktop Toolbar - bulk actions while selection mode */}
        {!isMobile ? (
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-between gap-2 px-3 sm:px-4 transition-all duration-300 ease-in-out",
              desktopSelectionMode ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-full opacity-0",
          )}
        >
          <div className={cn("flex min-w-0 flex-wrap items-center gap-2 md:gap-3", isMobile && "gap-2")}>
            <Button
              ref={bulkHeaderDismissRef}
              type="button"
              variant="outline"
              size="sm"
              className="h-9 w-9 shrink-0 p-0"
              aria-label={t("images.cancelSelection")}
              onClick={exitPhotoSelectionMode}
            >
              <X className="h-4 w-4" />
            </Button>
            <SelectionLiveRegion count={selectedPhotos.size} />
            <span className="text-sm font-medium tabular-nums">
              {t("images.selectedCount", { count: selectedPhotos.size })}
            </span>
            {selectedPhotos.size === 0 ? (
              <span className="hidden text-sm text-muted-foreground sm:inline">{t("common.selectItemsHint")}</span>
            ) : null}
            <button
              type="button"
              className="shrink-0 text-sm text-primary underline-offset-4 hover:underline disabled:pointer-events-none disabled:opacity-50"
              disabled={allPhotos.length === 0}
              onClick={() => setSelectedPhotos(new Set(allPhotos.map((p) => p.id)))}
            >
              {t("common.selectAll")}
            </button>
          </div>
          <div className="flex gap-2">
            {/* header-overflow: unified E */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 w-9 p-0"
                  aria-label={t("common.more")}
                  disabled={selectedPhotos.size === 0}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => handleBulkMove("bemeres")}
                  disabled={selectedPhotos.size === 0 || movePhotos.isPending}
                >
                  <MoveRight className="h-4 w-4 mr-2" />
                  {t("imagesFolderExtra.subfolderBemeres")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleBulkMove("munkaink")}
                  disabled={selectedPhotos.size === 0 || movePhotos.isPending}
                >
                  <MoveRight className="h-4 w-4 mr-2" />
                  {t("imagesFolderExtra.subfolderMunkaink")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleBulkMove("marketing")}
                  disabled={selectedPhotos.size === 0 || movePhotos.isPending}
                >
                  <MoveRight className="h-4 w-4 mr-2" />
                  {t("imagesFolderExtra.subfolderMarketing")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    const photoIds = Array.from(selectedPhotos);
                    const photosToDownload = allPhotos.filter((p) => photoIds.includes(p.id));
                    if (photosToDownload.length === 0) return;
                    setIsDownloading(true);
                    try {
                      if (photosToDownload.length === 1) {
                        const photo = photosToDownload[0];
                        toast({
                          title: t("imagesFolderExtra.downloadInProgress"),
                          description: `A kép letöltése megkezdődött: ${photo.file_name}`,
                        });
                        const response = await fetch(photo.file_url);
                        if (!response.ok) throw new Error(t("imagesFolderExtra2.downloadFailed"));
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = photo.file_name;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(url);
                        toast({
                          title: "Letöltés sikeres",
                          description: `A kép sikeresen letöltve: ${photo.file_name}`,
                        });
                      } else {
                        toast({
                          title: t("imagesFolderExtra.zipGenerating"),
                          description: `${photosToDownload.length} kép ZIP fájlba csomagolása folyamatban. Ez eltarthat egy ideig.`,
                        });
                        const response = await fetch(apiUrl(`/api/images/folders/${folderId}/photos/download`), {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ photoIds }),
                        });
                        if (!response.ok) {
                          const errorData = await response.json().catch(() => ({ error: "unknown_error" }));
                          throw new Error(errorData.message || `Server error: ${response.status}`);
                        }
                        const zipBlob = await response.blob();
                        const contentDisposition = response.headers.get("Content-Disposition");
                        let filename = `${folder.name || "images"}_${Date.now()}.zip`;
                        if (contentDisposition) {
                          const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
                          if (filenameMatch) filename = filenameMatch[1];
                        }
                        const url = window.URL.createObjectURL(zipBlob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(url);
                        toast({
                          title: "Letöltés sikeres",
                          description: `${photosToDownload.length} kép sikeresen letöltve ZIP fájlként.`,
                        });
                      }
                    } catch (error) {
                      console.error("Error downloading ZIP:", error);
                      toast({
                        title: t("imagesFolderExtra.downloadFailed"),
                        description: error instanceof Error ? error.message : t("imagesFolderExtra.zipDownloadError"),
                        variant: "destructive",
                      });
                    } finally {
                      setIsDownloading(false);
                    }
                  }}
                  disabled={selectedPhotos.size === 0 || isDownloading}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Letöltés ({selectedPhotos.size})
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleBulkDelete}
                  disabled={selectedPhotos.size === 0 || bulkDelete.isPending}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("imagesFolder.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        ) : null}
      </div>

      {isMobile && (
        <>
          {/* fab-token: unified D */}
          <FabMenu
            hidden={selectionModeEnabled}
            actions={[
              {
                id: "edit-folder",
                label: t("common.edit"),
                icon: <Pencil className="h-5 w-5" />,
                onClick: () => setShowEditDialog(true),
              },
              ...(isAdmin
                ? [
                    {
                      id: "resend-marketing-webhook",
                      label: t("imagesFolder.resendMarketingWebhook"),
                      icon: <Upload className="h-5 w-5" />,
                      onClick: () => setShowMarketingWebhookDialog(true),
                    },
                  ]
                : []),
              {
                id: "upload-bemeres",
                label: t("imagesFolderExtra.subfolderBemeres"),
                icon: <Upload className="h-5 w-5" />,
                onClick: () => {
                  setUploadSubfolderType("bemeres");
                  fileInputRef.current?.click();
                },
              },
              {
                id: "upload-munkaink",
                label: t("imagesFolderExtra.subfolderMunkaink"),
                icon: <Upload className="h-5 w-5" />,
                onClick: () => {
                  setUploadSubfolderType("munkaink");
                  fileInputRef.current?.click();
                },
              },
              {
                id: "upload-marketing",
                label: t("imagesFolderExtra.subfolderMarketing"),
                icon: <Upload className="h-5 w-5" />,
                onClick: () => {
                  setUploadSubfolderType("marketing");
                  fileInputRef.current?.click();
                },
              },
            ]}
          />
        </>
      )}

      {isMobile && selectionModeEnabled && (
        <div className="fixed inset-x-0 z-40 px-3 pb-2" style={{ bottom: "calc(4rem + env(safe-area-inset-bottom))" }}>
          <div className="mx-auto flex max-w-xl flex-wrap items-center gap-2 rounded-xl border bg-background/95 px-3 py-2 shadow-lg backdrop-blur">
            <Button
              ref={bulkHeaderDismissRef}
              type="button"
              variant="outline"
              size="sm"
              className="h-9 w-9 shrink-0 p-0"
              aria-label={t("images.cancelSelection")}
              onClick={() => {
                hapticTap(12);
                exitPhotoSelectionMode();
              }}
            >
              <X className="h-4 w-4" />
            </Button>
            <SelectionLiveRegion count={selectedPhotos.size} />
            <span className="text-sm font-medium tabular-nums">
              {t("images.selectedCount", { count: selectedPhotos.size })}
            </span>
            {selectedPhotos.size === 0 ? (
              <span className="text-sm text-muted-foreground">{t("common.selectItemsHint")}</span>
            ) : null}
            <button
              type="button"
              className="shrink-0 text-sm text-primary underline-offset-4 hover:underline disabled:pointer-events-none disabled:opacity-50 hidden sm:inline"
              disabled={allPhotos.length === 0}
              onClick={() => setSelectedPhotos(new Set(allPhotos.map((p) => p.id)))}
            >
              {t("common.selectAll")}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 w-9 shrink-0 p-0 ml-auto"
                  aria-label={t("common.more")}
                  disabled={selectedPhotos.size === 0}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => setSelectedPhotos(new Set(allPhotos.map((p) => p.id)))}
                  disabled={allPhotos.length === 0}
                >
                  {t("common.selectAll")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => handleBulkMove("bemeres")}
                  disabled={selectedPhotos.size === 0 || movePhotos.isPending}
                >
                  <MoveRight className="h-4 w-4 mr-2" />
                  {t("imagesFolderExtra.subfolderBemeres")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleBulkMove("munkaink")}
                  disabled={selectedPhotos.size === 0 || movePhotos.isPending}
                >
                  <MoveRight className="h-4 w-4 mr-2" />
                  {t("imagesFolderExtra.subfolderMunkaink")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleBulkMove("marketing")}
                  disabled={selectedPhotos.size === 0 || movePhotos.isPending}
                >
                  <MoveRight className="h-4 w-4 mr-2" />
                  {t("imagesFolderExtra.subfolderMarketing")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleBulkDelete}
                  disabled={selectedPhotos.size === 0 || bulkDelete.isPending}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("imagesFolder.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}

      {/* Photos Grid */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="grid h-full min-h-0 grid-cols-1 md:grid-cols-[340px,1fr] gap-3 md:gap-6">
        <div className="hidden md:block overflow-y-auto">
          <div className="p-4 space-y-4">
            <div className="p-4 border rounded-md bg-card space-y-4">
              <Input
                type="text"
                placeholder={t("images.searchPlaceholder")}
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
              />
            </div>
            <div className="p-4 border rounded-md bg-card">
              <ImageFilters
                filters={filters}
                onFiltersChange={setFilters}
                minDate={dateRangeQuery.data?.minDate}
                maxDate={dateRangeQuery.data?.maxDate}
                dateFilterEnabled={dateFilterEnabled}
                onDateFilterToggle={setDateFilterEnabled}
                optionCounts={galleryFacetsQuery.data}
              />
            </div>
          </div>
        </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div
          ref={gridContainerRef}
          className={cn(
            "relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto",
            fileDragHighlight && "ring-2 ring-primary ring-inset"
          )}
          onDragEnter={(e) => {
            if (!dataTransferHasFilePayload(e.dataTransfer)) return;
            e.preventDefault();
            setFileDragHighlight(true);
          }}
          onDragLeave={(e) => {
            if (!dataTransferHasFilePayload(e.dataTransfer)) return;
            const next = e.relatedTarget as Node | null;
            if (next && (e.currentTarget as Node).contains(next)) return;
            setFileDragHighlight(false);
          }}
          onDragOver={(e) => {
            if (!dataTransferHasFilePayload(e.dataTransfer)) return;
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            setFileDragHighlight(false);
            if (!dataTransferHasFilePayload(e.dataTransfer)) return;
            e.preventDefault();
            e.stopPropagation();
            const list = filterImageFiles(Array.from(e.dataTransfer.files));
            if (list.length === 0) return;
            openUploadTargetDialog(list, uploadSubfolderType);
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          {fileDragHighlight && (
            <div className="pointer-events-none absolute inset-0 z-[30] flex items-center justify-center bg-background/70 backdrop-blur-[1px]">
              <p className="rounded-lg border bg-card px-4 py-3 text-center text-sm font-medium shadow-lg">
                {t("imagesFolder.dropReleaseHint")}
              </p>
            </div>
          )}
          <div className="container mx-auto w-full px-2 py-3 md:px-4 md:py-6">
            <ImagesSectionTabs active="folders" compact={isMobile} />
            <div className="mt-4 bg-background p-6">
        {/* Drag selection box */}
        {isDragging && dragStart && dragEnd && (
          <div
            className="absolute border-2 border-primary bg-primary/10 pointer-events-none z-50"
            style={{
              left: Math.min(dragStart.x, dragEnd.x),
              top: Math.min(dragStart.y, dragEnd.y),
              width: Math.abs(dragEnd.x - dragStart.x),
              height: Math.abs(dragEnd.y - dragStart.y),
            }}
          />
        )}
        {/* Upload Queue */}
        {uploadQueue.length > 0 && (
          <div className="mb-4 p-4 bg-muted rounded-lg border-2 border-dashed border-primary/50">
            <div className="text-sm font-medium mb-2">{t("imagesFolder.uploadInProgress")}</div>
            <div className="space-y-2">
              {uploadQueue.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-2 bg-background rounded border"
                >
                  <div className="truncate pr-2 text-sm flex-1">{item.file.name}</div>
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium",
                        item.status === "done"
                          ? "bg-green-600 text-white"
                          : item.status === "error"
                            ? "bg-red-600 text-white"
                            : item.status === "uploading"
                              ? "bg-blue-600 text-white"
                              : "bg-muted text-foreground"
                      )}
                    >
                      {item.status === "uploading"
                        ? `${item.progress}%`
                        : item.status === "done"
                          ? "✓"
                          : item.status === "error"
                            ? "!"
                            : "•"}
                    </div>
                    {item.status === "error" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setUploadQueue((q) => q.filter((i) => i.id !== item.id));
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {folderMatchesFilters ? (
        <>
        <PhotoGrid
          scrollRef={gridContainerRef}
          columnCount={6}
          photos={bemeresPhotosWithLabels}
          subfolderType="bemeres"
          sectionTitle={t("imagesFolderExtra.subfolderBemeres")}
          selectedPhotos={selectedPhotos}
          onSelectPhoto={handleSelectPhoto}
          selectionMode={selectionMode}
          selectionModeEnabled={selectionModeEnabled}
          isMobile={isMobile}
          onEnterSelectionMode={(photoId) => {
            hapticTap(12);
            setSelectionModeEnabled(true);
            setSelectedPhotos(new Set([photoId]));
          }}
          onPhotoClick={(photo, index) => {
            const allBemeres = folder.photos.bemeres || [];
            const globalIndex = allBemeres.findIndex((p) => p.id === photo.id);
            handlePhotoClick(photo, globalIndex >= 0 ? globalIndex : 0, allPhotos);
          }}
          onUploadClick={() => {
            setUploadSubfolderType("bemeres");
            fileInputRef.current?.click();
          }}
          onDropFiles={(files, subfolderType) => {
            openUploadTargetDialog(Array.from(files), subfolderType);
          }}
          getPhotoLabel={(p) => (p as ImageFolderPhoto & { _displayLabel?: string })._displayLabel ?? ""}
        />
        <PhotoGrid
          scrollRef={gridContainerRef}
          columnCount={6}
          photos={folder.photos.munkaink || []}
          subfolderType="munkaink"
          selectedPhotos={selectedPhotos}
          onSelectPhoto={handleSelectPhoto}
          selectionMode={selectionMode}
          selectionModeEnabled={selectionModeEnabled}
          isMobile={isMobile}
          onEnterSelectionMode={(photoId) => {
            hapticTap(12);
            setSelectionModeEnabled(true);
            setSelectedPhotos(new Set([photoId]));
          }}
          onPhotoClick={(photo, index) => {
            const allBemeres = folder.photos.bemeres || [];
            const allMunkaink = folder.photos.munkaink || [];
            const globalIndex = allBemeres.length + allMunkaink.findIndex((p) => p.id === photo.id);
            handlePhotoClick(photo, globalIndex, allPhotos);
          }}
          onUploadClick={() => {
            setUploadSubfolderType("munkaink");
            fileInputRef.current?.click();
          }}
          onDropFiles={(files, subfolderType) => {
            openUploadTargetDialog(Array.from(files), subfolderType);
          }}
        />
        <PhotoGrid
          scrollRef={gridContainerRef}
          columnCount={6}
          photos={folder.photos.marketing || []}
          subfolderType="marketing"
          selectedPhotos={selectedPhotos}
          onSelectPhoto={handleSelectPhoto}
          selectionMode={selectionMode}
          selectionModeEnabled={selectionModeEnabled}
          isMobile={isMobile}
          onEnterSelectionMode={(photoId) => {
            hapticTap(12);
            setSelectionModeEnabled(true);
            setSelectedPhotos(new Set([photoId]));
          }}
          onPhotoClick={(photo, index) => {
            const allBemeres = folder.photos.bemeres || [];
            const allMunkaink = folder.photos.munkaink || [];
            const allMarketing = folder.photos.marketing || [];
            const globalIndex = allBemeres.length + allMunkaink.length + allMarketing.findIndex((p) => p.id === photo.id);
            handlePhotoClick(photo, globalIndex, allPhotos);
          }}
          onUploadClick={() => {
            setUploadSubfolderType("marketing");
            fileInputRef.current?.click();
          }}
          onDropFiles={(files, subfolderType) => {
            openUploadTargetDialog(Array.from(files), subfolderType);
          }}
        />
        </>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
            {t("common.noResults")}
          </div>
        )}
            </div>
          </div>
        </div>
      </div>
        </div>
      </div>

      {/* Photo Viewer */}
      {viewerPhoto && (
        <PhotoViewer
          photos={viewerPhoto.allPhotos}
          currentIndex={viewerPhoto.index}
          onClose={() => setViewerPhoto(null)}
          onNavigate={handleViewerNavigate}
          getPhotoLabel={(p) => photoLabelById.get(p.id) ?? ""}
        />
      )}

      <Dialog open={showMarketingWebhookDialog} onOpenChange={setShowMarketingWebhookDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("imagesFolder.resendMarketingWebhook")}</DialogTitle>
            <DialogDescription>{t("imagesFolder.resendMarketingWebhookConfirm")}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              disabled={marketingWebhookSending}
              onClick={() => setShowMarketingWebhookDialog(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              disabled={marketingWebhookSending || !folderId}
              onClick={async () => {
                if (!folderId) return;
                setMarketingWebhookSending(true);
                try {
                  const r = await fetch(apiUrl("/api/admin/marketing-webhook/resend"), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ folderId }),
                  });
                  const j = await r.json().catch(() => ({}));
                  if (!r.ok || j.ok === false) {
                    notify.error(
                      t("imagesFolder.resendMarketingWebhookFailed"),
                      typeof j.error === "string" ? j.error : "",
                    );
                    return;
                  }
                  notify.success(t("imagesFolder.resendMarketingWebhookSuccess"), "");
                  setShowMarketingWebhookDialog(false);
                  void folderQuery.refetch();
                } catch (e) {
                  notify.error(
                    t("imagesFolder.resendMarketingWebhookFailed"),
                    e instanceof Error ? e.message : "",
                  );
                } finally {
                  setMarketingWebhookSending(false);
                }
              }}
            >
              {marketingWebhookSending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("imagesFolder.resendMarketingWebhookSending")}
                </>
              ) : (
                t("imagesFolder.resendMarketingWebhook")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Folder Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("imagesFolder.editFolder")}</DialogTitle>
            <DialogDescription>
              Módosítsa a mappa adatait és metadata mezőit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">{t("imagesFolder.folderName")}</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <LookupSelect
                  id="edit-panelStructure"
                  type="panel_structure"
                  value={editForm.panelStructure}
                  onValueChange={(value) => setEditForm({ ...editForm, panelStructure: value })}
                  label={t("imagesExtra.surfaceLabel")}
                  placeholder={t("images.dimensionsPlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <LookupSelect
                  id="edit-brand"
                  type="brand"
                  value={editForm.brand}
                  onValueChange={(value) => setEditForm({ ...editForm, brand: value })}
                  label={t("imagesExtra.brandLabel")}
                />
              </div>
              <div className="space-y-2">
                <LookupSelect
                  id="edit-color"
                  type="color"
                  value={editForm.color}
                  onValueChange={(value) => setEditForm({ ...editForm, color: value })}
                  label={t("imagesExtra.colorLabel")}
                />
              </div>
              <div className="space-y-2">
                <LookupSelect
                  id="edit-prelisType"
                  type="prelis"
                  value={editForm.prelisType}
                  onValueChange={(value) => setEditForm({ ...editForm, prelisType: value || "" })}
                  label={t("imagesExtra.prelisLabel")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-dealId">{t("imagesFolderExtra.dealLabel")}</Label>
                <Popover open={editDealSearchOpen} onOpenChange={setEditDealSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={editDealSearchOpen}
                      className="w-full justify-between"
                    >
                      {editForm.dealId
                        ? (Array.isArray(dealsQuery.data) ? dealsQuery.data.find((d) => d.id === editForm.dealId)?.title : null) || t("imagesExtra.dealSelected")
                        : t("imagesExtra.selectDealPlaceholder")}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder={t("images.searchDealPlaceholder")}
                        value={editDealSearchValue}
                        onValueChange={setEditDealSearchValue}
                      />
                      <CommandList>
                        <CommandEmpty>{t("common.noResults")}</CommandEmpty>
                        <CommandGroup>
                          {(() => {
                            const deals = Array.isArray(dealsQuery.data) ? dealsQuery.data : [];
                            const searchLower = editDealSearchValue.toLowerCase();
                            const filtered = deals.filter((deal) => {
                              const title = deal.title?.toLowerCase() || "";
                              const matches = title.includes(searchLower);
                              return matches;
                            });
                            console.log("[Deals Search] Edit dialog:", { 
                              totalDeals: deals.length, 
                              searchValue: editDealSearchValue,
                              filteredCount: filtered.length 
                            });
                            return filtered;
                          })().map((deal) => (
                              <CommandItem
                                key={deal.id}
                                value={deal.id}
                                onSelect={() => {
                                  setEditForm({ ...editForm, dealId: deal.id });
                                  setEditDealSearchOpen(false);
                                  setEditDealSearchValue("");
                                  loadDealData(deal.id);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    editForm.dealId === deal.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {deal.title}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {editForm.dealId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditForm({ ...editForm, dealId: "" });
                    }}
                    className="mt-1"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Deal eltávolítása
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-installDate">{t("imagesFolderExtra.installDateLabel")}</Label>
                <DatePickerPopover
                  id="edit-installDate"
                  value={editForm.installDate}
                  onChange={(v) => setEditForm({ ...editForm, installDate: v })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-category">{t("imagesFolderExtra.categoryLabel")}</Label>
                <Select
                  value={editForm.category}
                  onValueChange={(value: ImageFolderCategory) => setEditForm({ ...editForm, category: value })}
                >
                  <SelectTrigger id="edit-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bemeres">{t("imagesFolderExtra.categoryBemeres")}</SelectItem>
                    <SelectItem value="realizacio">{t("imagesFolderExtra.categoryRealizacio")}</SelectItem>
                    <SelectItem value="egyeb">{t("imagesFolderExtra.categoryEgyeb")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              {t("imagesFolder.cancel")}
            </Button>
            <Button
              onClick={() => {
                if (!editForm.name.trim()) {
                  alert("A mappa neve kötelező");
                  return;
                }
                let nameToSave = editForm.name.trim();
                if (editForm.category === "realizacio" && editForm.installDate && editForm.dealId) {
                  const dealTitle = Array.isArray(dealsQuery.data) ? dealsQuery.data.find((d) => d.id === editForm.dealId)?.title : null;
                  if (dealTitle) {
                    nameToSave = `${editForm.installDate} ${dealTitle}`;
                  }
                }
                updateFolder.mutate({
                  name: nameToSave,
                  panelStructure: editForm.panelStructure || null,
                  color: editForm.color || null,
                  prelisType: editForm.prelisType && editForm.prelisType.trim() ? editForm.prelisType : null,
                  brand: editForm.brand || null,
                  installDate: editForm.installDate || null,
                  dealId: editForm.dealId || null,
                  category: editForm.category || "bemeres",
                });
              }}
              disabled={updateFolder.isPending}
            >
              {updateFolder.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("imagesFolder.saving")}
                </>
              ) : (
                t("imagesFolder.save")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={uploadTargetDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeUploadTargetDialog();
          else setUploadTargetDialogOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("imagesFolder.uploadTargetTitle")}</DialogTitle>
            <DialogDescription>
              {t("imagesFolder.uploadTargetDescription", { count: pendingDialogUploadFiles.length })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>{t("images.uploadSubfolderType")}</Label>
              <Select
                value={dialogTargetSubfolder}
                onValueChange={(v: SubfolderType) => setDialogTargetSubfolder(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bemeres">{t("images.bemeres")}</SelectItem>
                  <SelectItem value="munkaink">{t("images.munkaink")}</SelectItem>
                  <SelectItem value="marketing">{t("images.marketing")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              type="button"
              className="w-full"
              onClick={confirmUploadToCurrentFolder}
              disabled={galleryOnlyUploadBusy || uploadPhotos.isPending}
            >
              {t("imagesFolder.uploadTargetToThisFolder")}
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={() => void confirmUploadToGalleryOnly()}
              disabled={galleryOnlyUploadBusy || uploadPhotos.isPending}
            >
              {galleryOnlyUploadBusy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("imagesFolder.uploading")}
                </>
              ) : (
                t("imagesFolder.uploadTargetToGalleryOnly")
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={closeUploadTargetDialog}
              disabled={galleryOnlyUploadBusy}
            >
              {t("imagesFolder.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AppSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        title={t("common.filterLabel")}
      >
        <div className="space-y-4 py-4">
          <Input
            type="text"
            placeholder={t("images.searchPlaceholder")}
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
          <ImageFilters
            filters={filters}
            onFiltersChange={setFilters}
            minDate={dateRangeQuery.data?.minDate}
            maxDate={dateRangeQuery.data?.maxDate}
            dateFilterEnabled={dateFilterEnabled}
            onDateFilterToggle={setDateFilterEnabled}
            optionCounts={galleryFacetsQuery.data}
          />
        </div>
      </AppSheet>
    </div>
  );
}
